/**
 * Who charges a subscription, in plain words (Brandon's decision, 2026-11): plans started in Keap finish in
 * Keap (the CRM never takes them over), and Club memberships stay billed by Keap until Brandon moves each
 * member to CRM billing. The server refuses changes, cancels and refunds on anything Keap bills.
 */
export interface KeapBilling {
  label: string;
  tip: string;
  /** Why the CRM's change/cancel/refund buttons are off for it. */
  locked: string;
}

/** billed_by: crm | keap | moving (older payloads only send billing_enabled). club: a Club membership. */
export function keapBilling(s: { billed_by?: string | null; billing_enabled?: boolean | null }, club: boolean): KeapBilling | null {
  const by = s.billed_by || (s.billing_enabled === false ? "keap" : "crm");
  if (by === "moving")
    return {
      label: "Moving from Keap",
      tip: "Being moved to CRM billing. Keap still bills it until Keap's billing shows off; change it after the move (or undo the move).",
      locked: "It's moving from Keap to the CRM: change it after the move finishes.",
    };
  if (by !== "keap") return null;
  return club
    ? {
        label: "Billed in Keap until moved",
        tip: "Keap keeps charging this Club membership until Brandon moves the member to CRM billing. Until then change, refund or cancel it in Keap.",
        locked: "Keap bills this Club membership: change it in Keap, or move the member to CRM billing first.",
      }
    : {
        label: "Billed in Keap (finishes there)",
        tip: "Started in Keap, so Keap charges every payment until it ends; the CRM never takes it over. Change, refund or cancel it in Keap.",
        locked: "Keap bills this plan until it ends: change, refund or cancel it in Keap.",
      };
}

/** Gateways the CRM can refund through (internal/billing/refunds.go); anything else is refused by the server. */
const REFUNDABLE = new Set(["authorize_net", "payflow", "paypal", "stripe"]);

/** Why a payment can't be refunded from the CRM, or null when it can. */
export function refundBlocked(gateway: string | null | undefined): string | null {
  if (!gateway || REFUNDABLE.has(gateway)) return null;
  return gateway === "keap" ? "Taken by Keap: refund it in Keap" : "Recorded by hand: refund it outside the CRM";
}
